/* eslint-disable indent */
import { Popover, Transition } from '@headlessui/react';
import React, { FC, Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { DateRangePicker, StaticRange, createStaticRanges, defaultStaticRanges } from 'react-date-range';

import DateUtils from 'utils/dateUtils'; // theme
import './DateRangePicker.css'; // main style file
import './DateRangePickerTheme.css';
// import 'react-date-range/dist/theme/default.css';
import { CrossIcon, DownArrowIcon, PlusIcon } from 'assets/icons';
import { twMerge } from 'tailwind-merge';
import classnames from 'classnames';

export interface CustomDateRangePickerProps {
  startDate: string | null;
  setStartDate: (startDate: string | null) => void;
  endDate: string | null;
  setEndDate: (endDate: string | null) => void;
  customClassName?: string;
  filterId: string;
  placeholder?: string;
  allowFutureDate?: boolean;
  allowPastDate?: boolean;
  hasDefaultRanges?: boolean;
  customRanges?: StaticRange[];
  customTrigger?: ReactNode;
  emptyStateContent?: ReactNode;
  setDateRange?: (startDate: string | null, endDate: string | null) => void;
  anytimeContent?: ReactNode;
  customSecondaryText?: string;
  onClear?: () => void;
  renderContentOnly?: boolean;
  onContentClose?: () => void;
  onOpenDatePicker?: () => void;
  panelAlign?: 'left' | 'right';
}

const CustomDateRangePicker: FC<CustomDateRangePickerProps> = props => {
  const {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    customClassName,
    filterId,
    placeholder = 'Date',
    allowFutureDate = false,
    allowPastDate = true,
    hasDefaultRanges = true,
    customRanges = [],
    customTrigger,
    emptyStateContent,
    setDateRange,
    anytimeContent,
    customSecondaryText,
    onClear,
    renderContentOnly = false,
    onContentClose,
    onOpenDatePicker = () => null,
    panelAlign = 'left'
  } = props;

  const getDefaultRange = () => {
    if (startDate || endDate) {
      const stDate = startDate ? new Date(startDate) : new Date();
      let eDate;
      if (endDate) {
        eDate = new Date(endDate);
      } else if (startDate) {
        eDate = new Date(startDate);
      } else {
        eDate = new Date();
      }
      return [
        {
          startDate: stDate,
          endDate: eDate,
          key: 'rollup'
        }
      ];
    }

    const today = new Date();
    return [
      {
        startDate: today,
        endDate: today,
        key: 'rollup'
      }
    ];
  };

  const [ranges, setRanges] = useState(getDefaultRange());

  const panelId = useMemo(() => `${filterId}-date-range-panel`, [filterId]);

  const scheduleScrollSelectedDateRangeIntoView = () => {
    if (typeof window === 'undefined') return;

    // Wait for DOM + layout + selected class to settle
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollSelectedDateRangeIntoView();
      });
    });
  };

  const scrollSelectedDateRangeIntoView = () => {
    if (typeof window === 'undefined') return;

    const isShortViewport = window.matchMedia('(max-height: 900px)').matches;
    if (!isShortViewport) return;

    const panelElement = document.getElementById(panelId);
    if (!panelElement) return;

    const definedRangesWrapper = panelElement.querySelector('.rdrDefinedRangesWrapper') as HTMLElement | null;
    const selectedPreset = panelElement.querySelector('.rdrStaticRangeSelected') as HTMLElement | null;
    if (!definedRangesWrapper || !selectedPreset) return;

    if (definedRangesWrapper.scrollHeight <= definedRangesWrapper.clientHeight) return;

    selectedPreset.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  useEffect(() => {
    if (startDate || endDate) {
      const stDate = startDate ? DateUtils.getStartOfDay(startDate) : null;
      const eDate = endDate ? DateUtils.getEndOfDay(endDate) : null;

      if (startDate && !endDate) {
        const epochDate = new Date(0);
        const startDateObj = new Date(startDate);
        if (startDateObj.getTime() === epochDate.getTime()) {
          setRanges([
            {
              startDate: epochDate,
              endDate: null,
              key: 'rollup'
            }
          ]);
          return;
        }
      }

      if (!startDate && !endDate) {
        setRanges([
          {
            startDate: null,
            endDate: null,
            key: 'rollup'
          }
        ]);
        return;
      }

      setRanges([
        {
          startDate: stDate,
          endDate: eDate,
          key: 'rollup'
        }
      ]);
    }
  }, [startDate, endDate]);

  const checkCustomFieldSelected = (
    range: { startDate?: Date; endDate?: Date },
    filterStartDate: Date,
    filterEndDate: Date
  ) => {
    const currentStart = filterStartDate?.toDateString();
    const currentEnd = filterEndDate?.toDateString();
    const rangeStart = range.startDate?.toDateString();
    const rangeEnd = range.endDate?.toDateString();

    return currentStart === rangeStart && currentEnd === rangeEnd;
  };

  const getStaticRanges = () => {
    if (hasDefaultRanges) {
      const currentDate = new Date();
      const sixMonthsAgo = DateUtils.subtractMonthsFromDate(currentDate, 6);

      return [
        ...defaultStaticRanges,
        ...createStaticRanges([
          {
            label: 'Last 6 Months',
            isSelected: range => checkCustomFieldSelected(range, sixMonthsAgo, currentDate),
            range: () => ({
              startDate: sixMonthsAgo,
              endDate: currentDate
            })
          }
        ])
      ];
    }
    return [...createStaticRanges(customRanges)];
  };

  const applyFilter = () => {
    if (ranges.length) {
      const range = ranges[0];
      if (range.startDate && range.endDate) {
        const formattedStart = DateUtils.getDateInFormat({ date: range.startDate, dateFormat: 'yyyy-MM-dd' });
        const formattedEnd = DateUtils.getDateInFormat({ date: range.endDate, dateFormat: 'yyyy-MM-dd' });
        setStartDate(formattedStart);
        setEndDate(formattedEnd);
        setDateRange?.(formattedStart, formattedEnd);
      } else if (range.startDate) {
        const formattedStart = DateUtils.getDateInFormat({ date: range.startDate, dateFormat: 'yyyy-MM-dd' });
        setStartDate(formattedStart);
        setEndDate(null);
        setDateRange?.(formattedStart, null);
      } else {
        setStartDate(null);
        setEndDate(null);
        setDateRange?.(null, null);
      }
    }
  };

  const handleCancel = () => {
    const stDate = startDate ? new Date(startDate) : new Date();
    const eDate = endDate ? new Date(endDate) : new Date();
    setRanges([
      {
        startDate: stDate,
        endDate: eDate,
        key: 'rollup'
      }
    ]);
  };

  const isDateRangeSelected = startDate || endDate || customSecondaryText;

  const getFilterAppliedText = () => {
    if (customSecondaryText) return customSecondaryText;
    if (startDate === endDate) return startDate;
    if (!startDate) return `Till ${endDate}`;
    if (!endDate) return `From ${startDate}`;
    return `${startDate} to ${endDate}`;
  };

  const onDateRangeChange = (ranges_: any) => {
    setRanges([ranges_.rollup]);
  };

  const handleClear = e => {
    e.stopPropagation();
    setStartDate(null);
    setEndDate(null);
    if (onClear) {
      onClear();
    } else {
      setDateRange?.(null, null);
    }
    setRanges(getDefaultRange());
  };

  const renderDatePickerContent = () => {
    if ((!ranges[0].startDate && !ranges[0].endDate) || (ranges[0].startDate && !ranges[0].endDate)) {
      const isAnytime = ranges[0].startDate && !ranges[0].endDate;
      const isUnscheduled = !ranges[0].startDate && !ranges[0].endDate;
      return (
        <div className="rdrDateRangeWrapper flex">
          <div className="rdrDefinedRangesWrapper">
            {getStaticRanges().map((range, index) => {
              const isSelected = range.isSelected ? range.isSelected(ranges[0]) : false;

              return (
                <button
                  key={index}
                  className={classnames('rdrStaticRange w-full', { rdrStaticRangeSelected: isSelected })}
                  style={isSelected ? { color: '#A4D7FA' } : undefined}
                  onClick={() => {
                    const { startDate: newStartDate, endDate: newEndDate } = range.range(null);
                    setRanges([
                      {
                        startDate: newStartDate,
                        endDate: newEndDate,
                        key: 'rollup'
                      }
                    ]);
                  }}>
                  <span className="rdrStaticRangeLabel">{range.label}</span>
                </button>
              );
            })}
          </div>
          <div className="rdrCalendarWrapper m-auto h-full px-16">
            {isUnscheduled && <div className="bg-white p-6">{emptyStateContent || 'No date selected'}</div>}
            {isAnytime && <div className="bg-white p-6">{anytimeContent || 'No date selected'}</div>}
          </div>
        </div>
      );
    }

    return (
      <DateRangePicker
        startDatePlaceholder="Start Date"
        endDatePlaceholder="End Date"
        rangeColors={['#A4D7FA']}
        ranges={ranges}
        {...(!allowFutureDate && { maxDate: new Date() })}
        onChange={onDateRangeChange}
        staticRanges={getStaticRanges()}
        inputRanges={[]}
        {...(!allowPastDate && { minDate: new Date() })}
      />
    );
  };
  const popoverClassNames = customTrigger
    ? 'w-full'
    : twMerge(
        `shrink-0 rounded-md border
        bg-primary px-3 py-2 text-base font-semibold text-primaryText
        outline-none transition-all duration-150 ease-linear focus:outline-none
        ${isDateRangeSelected ? ' border-solid bg-secondaryBtn' : 'border-dashed'}`,
        classnames({ [customClassName]: customClassName })
      );

  const renderFooter = (onClose: () => void) => {
    return (
      <div className="flex justify-end bg-secondaryBtn py-3 px-4">
        <div
          id={`${filterId}-cancel-button`}
          onClick={() => {
            handleCancel();
            onClose?.();
          }}
          className="text-sbase font-bold	 text-accentText hover:cursor-pointer">
          Cancel
        </div>
        <div
          id={`${filterId}-apply-button`}
          onClick={() => {
            applyFilter();
            onClose?.();
          }}
          className="ml-5 text-sbase font-bold text-secondary hover:cursor-pointer">
          Apply
        </div>
      </div>
    );
  };

  if (renderContentOnly) {
    return (
      <>
        {renderDatePickerContent()}
        {renderFooter(onContentClose)}
      </>
    );
  }

  const panelPositionClass = panelAlign === 'right' ? '-left-4 md:left-auto md:right-0' : '-left-4 md:left-0';

  return (
    <div>
      <Popover className="md:relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={popoverClassNames}
              style={customTrigger ? {} : { borderColor: '#ACB5BD' }}
              onClick={() => {
                onOpenDatePicker?.();
              }}
              type="button">
              {customTrigger || (
                <div id={filterId} className={'flex shrink-0 flex-row items-center'}>
                  <div>{isDateRangeSelected ? <CrossIcon onClick={handleClear} /> : <PlusIcon />}</div>
                  <div id={`${filterId}-text`} className={'shrink-0 px-3 text-sbase'}>
                    {placeholder}
                    {isDateRangeSelected && (
                      <div className="hidden lg:inline">
                        <span> {' | '} </span>
                        <span className={'text-secondary'}>{getFilterAppliedText()}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <DownArrowIcon className={open ? 'rotate-180' : ''} />
                  </div>
                </div>
              )}
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              afterEnter={scheduleScrollSelectedDateRangeIntoView}>
              <Popover.Panel
                id={panelId}
                className={`absolute ${panelPositionClass} z-40 mt-[2px] min-w-[200px] border border-borderGray
                bg-primary px-4 shadow-md sm:px-0`}>
                <div className="flex flex-col">
                  <div>{renderDatePickerContent()}</div>
                  {renderFooter(close)}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default CustomDateRangePicker;
